import React, { useState, useRef } from 'react';
import { Photo } from '@/types';
import { photosApi } from '@/api';
import { FILE_UPLOAD } from '@/utils/constants';
import { formatFileSize, isValidImageFile } from '@/utils/helpers';
import PhotoAnnotator from './PhotoAnnotator';
import AnnotationOverlay from './AnnotationOverlay';
import ConfirmDialog from './ConfirmDialog';

interface PhotoUploadProps {
  reportItemId: string;
  photos: Photo[];
  onPhotosUpdate: (photos: Photo[]) => void;
}

interface UploadSlot {
  id: string;
  file: File;
  progress: number;
  status: 'uploading' | 'error';
  previewUrl: string;
}

const PhotoUpload: React.FC<PhotoUploadProps> = ({
  reportItemId,
  photos,
  onPhotosUpdate,
}) => {
  const [uploading, setUploading] = useState(false);
  const [uploadSlots, setUploadSlots] = useState<UploadSlot[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [annotatingPhoto, setAnnotatingPhoto] = useState<Photo | null>(null);
  const [photoPendingDelete, setPhotoPendingDelete] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const uploadSingleFile = async (slot: UploadSlot): Promise<Photo | null> => {
    try {
      const photo = await photosApi.uploadPresigned(
        reportItemId,
        slot.file,
        undefined,
        (progress) => {
          setUploadSlots(prev =>
            prev.map(s => s.id === slot.id ? { ...s, progress } : s)
          );
        },
      );
      setUploadSlots(prev => prev.filter(s => s.id !== slot.id));
      URL.revokeObjectURL(slot.previewUrl);
      return { ...photo, annotations: photo.annotations ?? [] };
    } catch {
      setUploadSlots(prev =>
        prev.map(s => s.id === slot.id ? { ...s, status: 'error', progress: 0 } : s)
      );
      return null;
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    setError(null);

    const invalidFiles = files.filter(file => !isValidImageFile(file));
    if (invalidFiles.length > 0) {
      setError('Some files are not valid images. Only JPEG, PNG, and WebP are allowed.');
      return;
    }

    const oversizedFiles = files.filter(file => file.size > FILE_UPLOAD.MAX_SIZE);
    if (oversizedFiles.length > 0) {
      setError(`Some files exceed the maximum size of ${formatFileSize(FILE_UPLOAD.MAX_SIZE)}`);
      return;
    }

    const newSlots: UploadSlot[] = files.map(file => ({
      id: crypto.randomUUID(),
      file,
      progress: 0,
      status: 'uploading' as const,
      previewUrl: URL.createObjectURL(file),
    }));

    setUploadSlots(prev => [...prev, ...newSlots]);
    setUploading(true);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    const uploadedPhotos: Photo[] = [];
    for (const slot of newSlots) {
      const photo = await uploadSingleFile(slot);
      if (photo) uploadedPhotos.push(photo);
    }

    if (uploadedPhotos.length > 0) {
      onPhotosUpdate([...photos, ...uploadedPhotos]);
    }

    setUploading(false);
  };

  const handleRetry = async (slotId: string) => {
    const slot = uploadSlots.find(s => s.id === slotId);
    if (!slot) return;

    setUploadSlots(prev =>
      prev.map(s => s.id === slotId ? { ...s, status: 'uploading', progress: 0 } : s)
    );

    const photo = await uploadSingleFile(slot);
    if (photo) {
      onPhotosUpdate([...photos, photo]);
    }
  };

  const handleDismissError = (slotId: string) => {
    const slot = uploadSlots.find(s => s.id === slotId);
    if (slot) URL.revokeObjectURL(slot.previewUrl);
    setUploadSlots(prev => prev.filter(s => s.id !== slotId));
  };

  const confirmDelete = async () => {
    const photoId = photoPendingDelete;
    if (!photoId) return;
    setPhotoPendingDelete(null);
    try {
      await photosApi.deletePhoto(photoId);
      onPhotosUpdate(photos.filter(p => p.id !== photoId));
    } catch (err) {
      console.error('Failed to delete photo:', err);
    }
  };

  const handleCaptionUpdate = async (photoId: string, caption: string) => {
    try {
      await photosApi.updatePhotoCaption(photoId, caption);
      onPhotosUpdate(
        photos.map(p => (p.id === photoId ? { ...p, caption } : p))
      );
    } catch (err) {
      console.error('Failed to update caption:', err);
    }
  };

  const handleAnnotationSave = (updatedPhoto: Photo) => {
    onPhotosUpdate(photos.map(p => p.id === updatedPhoto.id ? updatedPhoto : p));
    setAnnotatingPhoto(null);
  };

  const activeSlots = uploadSlots.filter(s => s.status === 'uploading');
  const errorSlots = uploadSlots.filter(s => s.status === 'error');

  return (
    <div className="space-y-4">
      {/* Upload Button */}
      <div>
        <input
          ref={fileInputRef}
          type="file"
          accept={FILE_UPLOAD.ALLOWED_EXTENSIONS.join(',')}
          multiple
          onChange={handleFileSelect}
          className="hidden"
        />
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="w-full px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-primary-500 hover:bg-primary-50 transition-colors text-gray-600 hover:text-primary-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {uploading ? (
            <span className="inline-flex items-center gap-2">
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              Uploading...
            </span>
          ) : (
            <span>+ Add Photos</span>
          )}
        </button>
      </div>

      {/* Validation Error */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* Photos Grid + Upload Placeholders */}
      {(photos.length > 0 || uploadSlots.length > 0) && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {photos.map((photo) => (
            <PhotoCard
              key={photo.id}
              photo={photo}
              onDelete={() => setPhotoPendingDelete(photo.id)}
              onCaptionUpdate={(caption) => handleCaptionUpdate(photo.id, caption)}
              onAnnotate={() => setAnnotatingPhoto(photo)}
            />
          ))}
          {activeSlots.map((slot) => (
            <UploadingCard key={slot.id} slot={slot} />
          ))}
          {errorSlots.map((slot) => (
            <ErrorCard
              key={slot.id}
              slot={slot}
              onRetry={() => handleRetry(slot.id)}
              onDismiss={() => handleDismissError(slot.id)}
            />
          ))}
        </div>
      )}

      {photos.length === 0 && uploadSlots.length === 0 && (
        <div className="text-center py-8 text-gray-500 text-sm">
          No photos uploaded yet
        </div>
      )}

      {/* Photo Annotator Modal */}
      {annotatingPhoto && (
        <PhotoAnnotator
          photo={annotatingPhoto}
          onClose={() => setAnnotatingPhoto(null)}
          onSave={handleAnnotationSave}
        />
      )}

      {/* Delete confirmation */}
      <ConfirmDialog
        isOpen={photoPendingDelete !== null}
        title="Delete photo?"
        message="This will permanently remove the photo and its annotations. This cannot be undone."
        confirmLabel="Delete"
        cancelLabel="Cancel"
        destructive
        onConfirm={confirmDelete}
        onCancel={() => setPhotoPendingDelete(null)}
      />
    </div>
  );
};

const UploadingCard: React.FC<{ slot: UploadSlot }> = ({ slot }) => (
  <div className="relative bg-white rounded-lg overflow-hidden shadow-sm">
    <div className="overflow-hidden bg-gray-100 relative">
      <img
        src={slot.previewUrl}
        alt="Uploading"
        className="w-full h-auto block opacity-50"
      />
      <div className="absolute inset-0 flex flex-col items-center justify-center bg-black bg-opacity-30">
        <svg className="animate-spin h-6 w-6 text-white" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
        {slot.progress > 0 && (
          <span className="text-white text-xs font-medium mt-1">{slot.progress}%</span>
        )}
      </div>
      {/* Progress bar along bottom edge */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-gray-300">
        <div
          className="h-full bg-primary-500 transition-all duration-300"
          style={{ width: `${slot.progress}%` }}
        />
      </div>
    </div>
    <div className="p-2">
      <p className="text-sm text-gray-400 truncate">Uploading...</p>
    </div>
  </div>
);

const ErrorCard: React.FC<{ slot: UploadSlot; onRetry: () => void; onDismiss: () => void }> = ({ slot, onRetry, onDismiss }) => (
  <div className="relative bg-white rounded-lg overflow-hidden shadow-sm border border-red-200">
    <div className="overflow-hidden bg-gray-100 relative">
      <img
        src={slot.previewUrl}
        alt="Failed upload"
        className="w-full h-auto block opacity-40"
      />
      <div className="absolute inset-0 flex flex-col items-center justify-center bg-red-900 bg-opacity-30 gap-2">
        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <button
          onClick={onRetry}
          className="px-3 py-1 bg-white text-red-700 text-xs font-medium rounded-full shadow hover:bg-gray-50"
        >
          Retry
        </button>
      </div>
    </div>
    <div className="p-2 flex items-center justify-between">
      <p className="text-xs text-red-600 font-medium">Upload failed</p>
      <button
        onClick={onDismiss}
        className="text-gray-400 hover:text-gray-600"
      >
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      </button>
    </div>
  </div>
);

interface PhotoCardProps {
  photo: Photo;
  onDelete: () => void;
  onCaptionUpdate: (caption: string) => void;
  onAnnotate: () => void;
}

const PhotoCard: React.FC<PhotoCardProps> = ({ photo, onDelete, onCaptionUpdate, onAnnotate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [caption, setCaption] = useState(photo.caption || '');

  const handleSave = () => {
    if (caption !== photo.caption) {
      onCaptionUpdate(caption);
    }
    setIsEditing(false);
  };

  return (
    <div className="group relative bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow">
      {/* Image */}
      <div className="overflow-hidden bg-gray-100 relative">
        <img
          src={photo.thumbnailUrl || photo.url}
          alt={photo.caption || 'Inspection photo'}
          className="w-full h-auto block"
        />
        <AnnotationOverlay annotations={photo.annotations ?? []} />
      </div>

      {/* Annotation count badge */}
      {(photo.annotations?.length ?? 0) > 0 && (
        <div className="absolute top-2 left-2 px-1.5 py-0.5 bg-primary-600 text-white text-xs font-medium rounded-full leading-none">
          {photo.annotations!.length}
        </div>
      )}

      {/* Delete Button */}
      <button
        onClick={onDelete}
        className="absolute top-2 right-2 p-1.5 bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-700"
      >
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
            clipRule="evenodd"
          />
        </svg>
      </button>

      {/* Annotate Button */}
      <button
        onClick={onAnnotate}
        className="absolute bottom-[52px] left-2 px-2 py-1 bg-black bg-opacity-50 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity hover:bg-opacity-70 flex items-center gap-1"
      >
        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
          <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
        </svg>
        Annotate
      </button>

      {/* Caption */}
      <div className="p-2">
        {isEditing ? (
          <input
            type="text"
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            onBlur={handleSave}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSave();
              if (e.key === 'Escape') {
                setCaption(photo.caption || '');
                setIsEditing(false);
              }
            }}
            autoFocus
            placeholder="Add caption..."
            className="w-full text-sm border-b border-primary-500 focus:outline-none"
          />
        ) : (
          <p
            onClick={() => setIsEditing(true)}
            className="text-sm text-gray-700 cursor-pointer hover:text-primary-600 truncate"
          >
            {photo.caption || 'Add caption...'}
          </p>
        )}
      </div>
    </div>
  );
};

export default PhotoUpload;
